import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { BrushStart } from "../../../models/chart_model";
import { HarayanaDiscomData } from "../../../models/rajasthan";
import {
  getHaryanaDiscomData,
  getHaryanaForecast,
} from "../../../Rest_api/RestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { H_CHART_TYPES } from "../const";
import { GROUP_BY } from "./Data";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";

let X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "dateIndex"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["dateIndex"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const UNITS = ["MW", "MWh"];

export function HaryanaTrend() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [data, setData] = useState<HarayanaDiscomData[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [selectedForecastType, setSelectedForecastType] = useState<any[]>([
    "Forecast D+1",
  ]);
  const [loading, setLoading] = useState(true);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const chartSuffixes = [
    { suffix: "demand", displayName: "Load", stackSuffix: "demand" },
    {
      suffix: "deviation",
      displayName: "Deviation %",
      stackSuffix: "deviation",
    },
  ];

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedXaxis: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string },
    ExchangeColors: string[]
  ) => {
    const displayKey = key
      .replace("_", " ")
      .replace("undefined", "")
      .replace(/T\+1/g, "D+1")
      .replace(/T\+2/g, "D+2");

    return {
      dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
      name: `${displayKey} ${displayName}`,
      yAxisId: suffix === "deviation" ? "right" : undefined,
      stroke: suffix === "deviation" ? "" : undefined,
      legendColor: undefined,
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  const [selectedSuffix, setSelectedSuffix] = useState(chartSuffixes);

  const _handlefetchData = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates) {
      return;
    }

    setLoading(true);

    try {
      const originalDates = DefaultDates[activeTab].defaultTrendDates;
      let processedDates = [...originalDates];

      const modelsToAdd = [];
      for (const date of originalDates) {
        const { label, value } = date;
        if (label.replace(" to ", ",") === value) {
          modelsToAdd.push({
            forecast: true,
            isFixed: false,
            label: label + " (Forecast)",
            value: value + ",Model 1",
            model: "Model 1",
          });
        }
      }

      processedDates = [...processedDates, ...modelsToAdd];

      const promises = processedDates.map(async (date: any) => {
        try {
          const [startDate, endDate, model] = date.value.split(",");

          if (model) {
            const forecastedData = await getHaryanaForecast(startDate, endDate);
            return forecastedData.map((item: any) => ({
              ...item,
              dates: date.label.replace(")", " ") + item.forcast_type + ")",
            }));
          } else {
            return (await getHaryanaDiscomData(startDate, endDate)).map(
              (item) => ({
                ...item,
                dates: date.label,
              })
            );
          }
        } catch (error) {
          console.error(`Error processing date ${date.label}:`, error);
          return [];
        }
      });

      const results = await Promise.all(promises);
      const newData = results.flat();

      if (newData.length === 0) {
        console.warn("No data returned from API calls");
      }

      let transformedData = newData.map((item) => {
        let monthValue = "Unknown";
        if (item.date) {
          const date = new Date(item.date);
          if (!isNaN(date.getTime())) {
            monthValue = date.toLocaleDateString("en-US", { month: "short" });
          }
        } else if (item.dates) {
          try {
            const dateMatch = item.dates.match(
              /(\d{4}-\d{2}-\d{2})|([A-Z]{3}-\d{2})/i
            );
            if (dateMatch) {
              const dateStr = dateMatch[0];
              const date = new Date(dateStr);
              if (!isNaN(date.getTime())) {
                monthValue = date.toLocaleDateString("en-US", {
                  month: "short",
                });
              }
            }
          } catch (e) {
            console.error("Error parsing date from dates field:", e);
          }
        }

        return {
          ...item,
          month: monthValue,
        };
      });

      setData([...transformedData]);
      setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
    } catch (error) {
      console.error("Error in data fetching:", error);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index]);
  }

  const handleKey = useCallback(() => {
    if (selectedBlock.length > 0) {
      if (selectedXaxis.key === "time") {
        return ["dateIndex", "block"];
      } else if (selectedXaxis.key === "date") {
        return ["dateIndex"];
      } else if (selectedXaxis.key === "month") {
        return ["month"];
      }
    } else {
      return selectedXaxis.values;
    }
  }, [selectedBlock, selectedXaxis]);

  useEffect(() => {
    let filteredData = data;
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((opt) => opt.value).includes(item.block)
      );
    }
    const par: ChartDataParams = {
      keys: handleKey() ?? [],
      tableKeys: ["dateIndex", "block", "month"],
      groupBy: ["dates"].concat([selectedGroupKeys.toLowerCase()]),
      volumeKey: ``,
      aggregationKeys: ["demand", "demand_d_plus_one", "demand_d_plus_two"].map(
        (item) => item + "_" + selectedUnit.toLowerCase()
      ),
      averageKeys: ["demand_mw", "demand_d_plus_one_mw", "demand_d_plus_two_mw"],
      averageDuplicationKeys: ["block", "dateIndex","month"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);

    // Calculate deviation % for both D+1 and D+2 forecast data
    const processedData = cData.data.map((dataPoint) => {
      const newDataPoint = { ...dataPoint };

      cData.keys.forEach((key) => {
        if (key.includes("Forecast")) {
          const actualDateKey = key
            .replace(" (Forecast D+1)", "")
            .replace(" (Forecast D+2)", "");
          
          const actualDemandKey = `${actualDateKey}_demand_${selectedUnit.toLowerCase()}`;
          const actualDemand = dataPoint[actualDemandKey] ?? 0;

          let forecastDemandKey = "";
          if (key.includes("D+1")) {
            forecastDemandKey = `${key}_demand_d_plus_one_${selectedUnit.toLowerCase()}`;
          } else if (key.includes("D+2")) {
            forecastDemandKey = `${key}_demand_d_plus_two_${selectedUnit.toLowerCase()}`;
          }

          const forecastDemand = dataPoint[forecastDemandKey] ?? 0;

          let deviation = 0;
          if (actualDemand !== 0 && forecastDemand !== 0) {
            deviation = (Math.abs(actualDemand - forecastDemand) / actualDemand) * 100;
          }

          const deviationKey = `${key}_deviation_${selectedUnit.toLowerCase()}`;
          newDataPoint[deviationKey] = deviation;
        }
      });

      return newDataPoint;
    });

    setTableData(cData.tableData || []);
    setChartData(processedData);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedGroupKeys,
    selectedXaxis,
    selectedUnit,
    selectedArea,
    selectedBlock,
    selectedSuffix,
    handleKey,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  function handleUnitChange(unitParam: string) {
    setSelectedUnit(unitParam);
  }

  function getLegends() {
    const legends = selectedSuffix
      .flatMap(({ suffix, displayName, stackSuffix }) =>
        chartKeys.map((key, index) => {
          if (suffix === "deviation" && !key.includes("Forecast")) {
            return null;
          }

          let actualSuffix = suffix;
          if (suffix === "demand") {
            if (key.includes("Forecast D+1")) {
              actualSuffix = "demand_d_plus_one";
            } else if (key.includes("Forecast D+2")) {
              actualSuffix = "demand_d_plus_two";
            }
          }

          return createChartObject(
            key,
            index,
            actualSuffix,
            displayName,
            stackSuffix,
            selectedXaxis,
            selectedChartType as any,
            ExchangeColors
          );
        })
      )
      .filter((item: any) => item !== null)
      .filter(
        (item: any) =>
          !(item.name?.includes("Forecast") && item.name.includes("Schedule"))
      )
      .filter((item: any) => {
        if (
          !item.name?.includes("Forecast") &&
          !item.name?.includes("Deviation %")
        ) {
          return true;
        }

        if (item.name?.includes("Deviation %")) {
          if (selectedForecastType.length === 0) {
            return false;
          }
          return selectedForecastType.some((type) => {
            const forecastNum = type.replace("Forecast ", "");
            return (
              item.name?.includes(forecastNum) ||
              item.name?.includes(forecastNum.replace("D+", "T+"))
            );
          });
        }

        if (item.name?.includes("Forecast")) {
          return selectedForecastType.some((type) => {
            const forecastNum = type.replace("Forecast ", "");
            return (
              item.name?.includes(forecastNum) ||
              item.name?.includes(forecastNum.replace("D+", "T+"))
            );
          });
        }

        return true;
      });

    return legends;
  }

  function summaryExtraKeys() {
    if (selectedXaxis.isTime) {
      return ["block", "dateIndex"];
    } else if (selectedXaxis.key === "date") {
      return ["dateIndex"];
    } else {
      return [selectedXaxis.values[0]];
    }
  }

  const summaryExtraLabels = selectedXaxis.values.map(
    (value) => (value === "date" ? "Date" : value)
  );

  const handlexDataKey = useCallback(() => {
    if (selectedXaxis.key === "time") {
      if (selectedBlock.length > 0) {
        return "block";
      }
      return "block";
    } else {
      return selectedXaxis.values[0];
    }
  }, [selectedXaxis.key, selectedXaxis.values, selectedBlock.length]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);
  const commonSummaryProps = {
    legends: getLegends() as any,
    data: chartData,
    tableData: TableData,
    block_key: "block",
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Haryana Load Trend Analysis",
      dates: DefaultDates[activeTab].defaultTrendDates,
    }),
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS.map((item) => item)}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => handleUnitChange(item)}
          />
          <FilterMenu
            title="Type"
            items={["Forecast D+1", "Forecast D+2"]}
            selectedItems={selectedForecastType}
            setSelectedItems={(selected: string[]) => {
              if (selected.length === 0 || selected.length > 2) {
                setSelectedForecastType(["Forecast D+1", "Forecast D+2"]);
                return;
              }
              setSelectedForecastType(selected);
            }}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">Haryana Load ({selectedUnit})</h2>
            <div className="flex gap-x-6 w-full justify-end">
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Select Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <button
                onClick={() => {
                  dataTab.label === "Chart" &&
                    downloadChartTableCSV(commonSummaryProps);

                  dataTab.label === "Summary" &&
                    downloadSummaryCSV(commonSummaryProps);

                  dataTab.label === "Table" &&
                    downloadChartTableCSV(commonSummaryProps);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedXaxis.isTime}
              onlyTitle={true}
              {...({ secondYAxisDomain: [0, 100] } as any)}
              barCategoryGap={10}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              {...({ secondYAxisLabel: "Deviation %" } as any)}
              legends={getLegends() as any}
              xDataKey={xDataKey}
              secondXDataKey={
                selectedXaxis.isTime && selectedBlock.length === 0
                  ? selectedXaxis.values[1]
                  : undefined
              }
              xTick={
                selectedXaxis.isTime && selectedBlock.length === 0
                  ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                  : undefined
              }
            />
          )}
          {dataTab.label === "Summary" && (
            <LegendSummaryTable {...commonSummaryProps} />
          )}
          {dataTab.label === "Table" && <ChartTable {...commonSummaryProps} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>HPPC (Actual Load) and GNA Energy (Forecast)</p>}
              text=" "
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}